import React, { useEffect, useRef, useState } from 'react';
import { useAide } from './AideContexte';
import './Aide.css';

/**
 * LE BOUTON « ? » DU BANDEAU — commun à tous les modules (étapes A1 → A3)
 * =======================================================================
 * Maquette : doc/maquettes/aide_contextuelle_maquette.html (bandeau, menu, bulle « Nouveau »)
 *
 * Le menu ne montre QUE ce qui fonctionne : Comprendre cet onglet (A1), Comprendre le processus métier (A2, si le
 * module en a un), Je veux… (s'il a des tâches), Quoi de neuf (s'il a des entrées), puis Proposer une amélioration (A3 ; « Signaler un manque » avant v99).
 * La PASTILLE compte les « Quoi de neuf » que le compte n'a pas lus. La BULLE « Nouveau : l'aide est ici » ne se
 * montre qu'UNE fois par compte (lectures serveur) : « J'ai compris », ou ouvrir le menu, la marque comme vue.
 */
function BoutonAide() {
    const aide = useAide();
    const [menu, setMenu] = useState(false);
    const zone = useRef(null);

    // Un clic hors du bouton et du menu ferme le menu.
    useEffect(() => {
        if (!menu) return undefined;
        const dehors = (e) => { if (zone.current && !zone.current.contains(e.target)) setMenu(false); };
        document.addEventListener('mousedown', dehors);
        return () => document.removeEventListener('mousedown', dehors);
    }, [menu]);

    if (!aide) return null;
    const { donnees, lectures, nonLus } = aide;
    const aDesProcessus = !!(donnees.PROCESSUS && Object.keys(donnees.PROCESSUS).length);
    const aDesTaches = !!(donnees.TACHES && donnees.TACHES.length);
    const aDuNeuf = !!(donnees.NEUF && donnees.NEUF.length);
    const bulle = !!lectures && !lectures.bulle_vue;

    const basculer = () => {
        if (bulle) aide.marquerBulleVue();
        setMenu((m) => !m);
    };
    const choisir = (faire) => () => { setMenu(false); faire(); };

    return (
        <div className="aide-zone" ref={zone}>
            <button type="button" className={'aide-btn' + (menu ? ' on' : '')} title="Aide (F1)" aria-label="Aide"
                    aria-haspopup="menu" aria-expanded={menu} onClick={basculer}>
                <i className="fa-solid fa-circle-question" />
                {nonLus > 0 && <span className="aide-pastille" title={`${nonLus} nouveauté${nonLus > 1 ? 's' : ''} non lue${nonLus > 1 ? 's' : ''}`}>{nonLus}</span>}
            </button>
            {menu && (
                <div className="aide-menu" role="menu">
                    <button type="button" role="menuitem" className="aide-menu-item" onClick={choisir(() => aide.ouvrirVolet('onglet'))}>
                        <i className="fa-solid fa-book-open aide-menu-ic" /> Comprendre cet onglet <span className="aide-kbd">F1</span>
                    </button>
                    {aDesProcessus && (
                        <button type="button" role="menuitem" className="aide-menu-item" onClick={choisir(() => aide.setProcessusOuvert(true))}>
                            <i className="fa-solid fa-diagram-project aide-menu-ic" /> Comprendre le processus métier
                        </button>
                    )}
                    {aDesTaches && (
                        <button type="button" role="menuitem" className="aide-menu-item" onClick={choisir(() => aide.ouvrirVolet('jeveux'))}>
                            <i className="fa-solid fa-magnifying-glass aide-menu-ic" /> Je veux…
                        </button>
                    )}
                    {aDuNeuf && (
                        <button type="button" role="menuitem" className="aide-menu-item" onClick={choisir(() => aide.ouvrirVolet('neuf'))}>
                            <i className="fa-solid fa-gift aide-menu-ic" /> Quoi de neuf
                            {nonLus > 0 && <span className="aide-cnt">{nonLus}</span>}
                        </button>
                    )}
                    <div className="aide-menu-sep" />
                    <button type="button" role="menuitem" className="aide-menu-item discret" onClick={choisir(() => aide.setSignalerOuvert(true))}>
                        <i className="fa-solid fa-lightbulb aide-menu-ic" /> Proposer une amélioration
                    </button>
                </div>
            )}
            {bulle && !menu && (
                <div className="aide-bulle" role="status">
                    <b className="aide-bulle-t">Nouveau</b>
                    L'aide est ici. Cliquez sur <i className="fa-solid fa-circle-question" /> ou appuyez sur <span className="aide-bulle-kbd">F1</span> pour
                    comprendre l'écran où vous êtes, voir le processus ou retrouver comment faire une tâche.
                    <div><button type="button" onClick={() => aide.marquerBulleVue()}>J'ai compris</button></div>
                </div>
            )}
        </div>
    );
}

export default BoutonAide;
